import { supabase } from "@/integrations/supabase/client";
import { getGuestSessionId } from "@/lib/guestTracking";

type ClickContext = {
  course_id?: string;
  chapter_id?: string;
  flashcard_id?: string;
};

const lastClickAtByKey: Record<string, number> = {};
const CLICK_RATE_LIMIT_MS = 750;

function getPageContext() {
  try {
    const url = new URL(window.location.href);
    return {
      page_path: url.pathname,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
    };
  } catch {
    return {
      page_path: window.location.pathname,
      timezone: null,
    };
  }
}

function getDeviceContext() {
  try {
    const nav: any = navigator;
    const conn: any = (nav && (nav.connection || nav.mozConnection || nav.webkitConnection)) || null;
    const prefersReducedMotion = (() => {
      try {
        return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      } catch {
        return null;
      }
    })();
    const colorScheme = (() => {
      try {
        if (!window.matchMedia) return null;
        if (window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
        if (window.matchMedia("(prefers-color-scheme: light)").matches) return "light";
        return null;
      } catch {
        return null;
      }
    })();

    return {
      user_agent: nav?.userAgent ? String(nav.userAgent) : null,
      screen_resolution: window?.screen ? `${window.screen.width}x${window.screen.height}` : null,
      viewport_size:
        typeof window !== "undefined" && window.innerWidth && window.innerHeight
          ? `${window.innerWidth}x${window.innerHeight}`
          : null,
      language: nav?.language ? String(nav.language) : null,
      platform: nav?.platform ? String(nav.platform) : null,
      color_scheme: colorScheme,
      reduced_motion: prefersReducedMotion,
      connection_effective_type: conn?.effectiveType ? String(conn.effectiveType) : null,
      device_memory_gb: typeof nav?.deviceMemory === "number" ? nav.deviceMemory : null,
      hardware_concurrency: typeof nav?.hardwareConcurrency === "number" ? nav.hardwareConcurrency : null,
    };
  } catch {
    return {};
  }
}

export function trackGuestClick(
  context: ClickContext = {},
  key: string = "click",
  metadata?: { element_text?: string; element_type?: string }
) {
  // Rate limit check is synchronous and fast
  const now = Date.now();
  if (lastClickAtByKey[key] && now - lastClickAtByKey[key] < CLICK_RATE_LIMIT_MS) return;
  lastClickAtByKey[key] = now;

  // Capture context synchronously (fast)
  const sessionId = getGuestSessionId();
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(sessionId)) return;

  const page = getPageContext();
  
  // Defer heavy device context and network call to avoid blocking INP
  const sendTracking = async () => {
    try {
      const device = getDeviceContext();
      await supabase.functions.invoke("track-guest-activity", {
        method: "POST",
        body: {
          event: {
            session_id: sessionId,
            activity_type: "click",
            flashcard_id: context.flashcard_id ?? null,
            chapter_id: context.chapter_id ?? null,
            course_id: context.course_id ?? null,
            metadata: metadata ?? null,
            ...page,
            ...device,
          },
        },
      });
    } catch {
      // Silent by design
    }
  };

  // Use requestIdleCallback to defer off main thread
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => { void sendTracking(); }, { timeout: 3000 });
  } else {
    setTimeout(() => { void sendTracking(); }, 0);
  }
}
